/**
 * KbWebService — browser-side KB / Search-Engine adapter.
 *
 * Wraps every HTTP call the kirigami-app KB feature needs against the
 * `/kbx/*` surface of the KB API (documented in
 * `docs/kb/knowledge-bases-migration-knowledge-base.md` and
 * `docs/kb/references-and-knowledge-base-kirigami-app.md`).
 *
 * What belongs here:
 * - KB CRUD + document list + 3-step upload chain + download URL
 * - Search-Engine CRUD + query
 *
 * What does NOT belong here:
 * - Word add-in flows
 * - Any Router / dialog / snackbar concerns
 * - View-model enrichment (stays in the app)
 *
 * Base URL is injected via `KB_API_BASE_URL` so the lib stays
 * environment-agnostic. The KB API lives on a separate subdomain from
 * the Registry API (`kbapi-*` vs `api-*`) — use the dedicated token.
 */
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, switchMap, throwError } from 'rxjs';
import { KB_API_BASE_URL } from './kb-api-base-url.token';

/**
 * HttpContext flag that auth interceptors can read to decide whether to
 * skip the Authorization header on a given request. The presigned-URL
 * PUT used inside `uploadFile` is the canonical use case — an
 * `Authorization` header would invalidate the S3/GCS signature.
 */
export const SKIP_AUTH_HEADER = new HttpContextToken<boolean>(() => false);

// ── DTOs ─────────────────────────────────────────────────────────────

export interface KnowledgeBaseDocDto {
  id?: string;
  filename?: string;
  status?: string;
  bucket?: string;
  created_at?: string | null;
}

export interface KnowledgeBaseDto {
  id?: string;
  pk?: string;
  sk?: string;
  subscription_id?: string;
  owner?: string;
  name?: string;
  description?: string;
  status?: string;
  bucket?: string;
  datastore_id?: string;
  docs?: KnowledgeBaseDocDto[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CreateKnowledgeBaseRequest {
  name: string;
  description: string;
  subscription_id: string;
  /**
   * Internal user identifier. The Kirigami Registry OpenAPI requires
   * both `user_id` and `owner` on KB creation — some backend variants
   * store the email in both. Clients typically pass the current user's
   * email here too.
   */
  user_id: string;
  owner: string;
  status?: string;
}

export interface UpdateKnowledgeBaseRequest {
  name?: string;
  description?: string;
  docs?: KnowledgeBaseDocDto[];
}

export interface PresignedUploadUrlResponse {
  presigned_url: string;
  content_type?: string;
}

export interface PresignedDownloadUrlResponse {
  presigned_url: string;
}

export interface SearchEngineDto {
  id?: string;
  pk?: string;
  sk?: string;
  subscription_id?: string;
  engine_id?: string;
  owner?: string;
  name?: string;
  description?: string;
  status?: string;
  datastore_ids?: string[];
  kbs?: KnowledgeBaseDto[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CreateSearchEngineRequest {
  name: string;
  description: string;
  datastore_ids: string[];
  subscription_id: string;
  owner: string;
  status?: string;
}

/** Citation marker returned alongside `answer_text`. */
export interface SearchCitationDto {
  /**
   * Character offset in `answer_text` where the cited passage begins.
   * Plumbed through but not yet consumed — reserved for future passage
   * highlighting in the answer or in the document viewer.
   */
  start_index?: number;
  /** Character index in `answer_text` where the marker should be spliced. */
  end_index: number;
  /**
   * Back-pointer into `references[]`. Some backend variants ship a
   * numeric index (`"0"`, `"1"`...), others ship the `uri`/`id` of the
   * referenced doc — the app handles both.
   */
  reference_id: string;
}

export interface SearchReferenceDto {
  title?: string;
  uri?: string;
  snippet?: string;
  content?: string;
  page?: number | string;
  /** Optional — some backends include it directly on the reference. */
  bucket?: string;
  filename?: string;
}

export interface SearchQueryResponse {
  answer_text?: string;
  citations?: SearchCitationDto[];
  references?: SearchReferenceDto[];
}

export interface KbUsageDimensionDto {
  used: number;
  limit: number;
}

export interface KbQueryUsageDto extends KbUsageDimensionDto {
  window: 'lifetime' | 'month';
  resets_at: string | null;
}

/**
 * How References queries are billed (C7): `caps` = fixed query caps (Free
 * lifetime cap, legacy paid payloads); `credits` = paid plans debiting the
 * shared credit balance per query.
 */
export type KbBillingMode = 'credits' | 'caps';

/** Shape of GET /kbx/usage — also embedded verbatim in 402 rejections. */
export interface KbUsageDto {
  plan: string;
  docs: KbUsageDimensionDto;
  /** Caps-mode query meter (Free lifetime cap). Null when billing_mode === 'credits'. */
  queries: KbQueryUsageDto | null;
  references_enabled: boolean;
  /** Omitted by pre-credits backends → callers default to 'caps'. */
  billing_mode?: KbBillingMode;
  /** Credits per query (paid plans; credits mode only). */
  credits_per_query?: number;
  /** Spendable integer credits (plan pool + personal wallet; credits mode only). */
  credits_available?: number;
  /**
   * `'FREE_PLAN_ALREADY_USED'` when References are blocked because this
   * email's one-time Free benefit went to a previous (deleted) account
   * (2026-07-23 free-plan-reuse spec). Absent otherwise.
   */
  restriction_reason?: string;
}

export const KB_QUOTA_EXCEEDED = 'references_quota_exceeded';
export const KB_ENGINE_EXISTS = 'references_engine_exists';
/** 404 — the engine id no longer exists server-side (torn down); drop caches and re-create. */
export const KB_ENGINE_MISSING = 'references_engine_missing';
/** 402 — the one-time Free benefit was already used by a previous account. */
export const KB_FREE_PLAN_USED = 'FREE_PLAN_ALREADY_USED';

/**
 * Typed transport error. The KB API signals quota rejections with HTTP 402 +
 * a machine-readable `code` and the full usage payload; consumers need the
 * status/code to distinguish quota gates from ordinary failures.
 */
export class KbApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly usage?: KbUsageDto,
  ) {
    super(message);
    this.name = 'KbApiError';
  }
}

// ── Service ──────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class KbWebService {
  readonly #http = inject(HttpClient);
  readonly #baseUrl = inject(KB_API_BASE_URL);

  #url(path: string): string {
    return `${this.#baseUrl}${path}`;
  }

  // ── KB CRUD ────────────────────────────────────────────────────────

  /** `GET /kbx/kbs` — list every KB visible to the authenticated user. */
  listKnowledgeBases(): Observable<KnowledgeBaseDto[]> {
    return this.#http
      .get<KnowledgeBaseDto[]>(this.#url('/kbx/kbs'))
      .pipe(catchError((err) => this.#throwHttp('list knowledge bases', err)));
  }

  /** `GET /kbx/kb/get/:kbId` — full KB with embedded `docs[]`. */
  getKnowledgeBase(kbId: string): Observable<KnowledgeBaseDto> {
    return this.#http
      .get<KnowledgeBaseDto>(this.#url(`/kbx/kb/get/${encodeURIComponent(kbId)}`))
      .pipe(catchError((err) => this.#throwHttp('load knowledge base', err)));
  }

  /** `POST /kbx/kb` — create a new KB in the given subscription. */
  createKnowledgeBase(payload: CreateKnowledgeBaseRequest): Observable<KnowledgeBaseDto> {
    return this.#http
      .post<KnowledgeBaseDto>(this.#url('/kbx/kb'), {
        status: 'draft',
        ...payload,
      })
      .pipe(catchError((err) => this.#throwHttp('create knowledge base', err)));
  }

  /** `PUT /kbx/kb/:kbId` — partial update (name/description/docs). */
  updateKnowledgeBase(
    kbId: string,
    payload: UpdateKnowledgeBaseRequest,
  ): Observable<KnowledgeBaseDto> {
    return this.#http
      .put<KnowledgeBaseDto>(this.#url(`/kbx/kb/${encodeURIComponent(kbId)}`), payload)
      .pipe(catchError((err) => this.#throwHttp('update knowledge base', err)));
  }

  /** `DELETE /kbx/kb/:kbId`. */
  deleteKnowledgeBase(kbId: string): Observable<void> {
    return this.#http
      .delete<void>(this.#url(`/kbx/kb/${encodeURIComponent(kbId)}`))
      .pipe(catchError((err) => this.#throwHttp('delete knowledge base', err)));
  }

  // ── Upload chain ───────────────────────────────────────────────────

  /** Step 1 — `POST /kbx/kb/get-upload-url/:kbId` → presigned URL. */
  getUploadUrl(kbId: string, filename: string): Observable<PresignedUploadUrlResponse> {
    return this.#http
      .post<PresignedUploadUrlResponse>(
        this.#url(`/kbx/kb/get-upload-url/${encodeURIComponent(kbId)}`),
        { filename },
      )
      .pipe(catchError((err) => this.#throwHttp('prepare KB document upload', err)));
  }

  /**
   * Step 2 — raw PUT to the presigned URL.
   * Uses XHR directly so the functional auth interceptor cannot add an
   * Authorization header (which would break the S3/GCS signature). The
   * interceptor in the app also honours the `SKIP_AUTH_HEADER` context
   * flag, but XHR is the belt-and-suspenders choice.
   */
  uploadFile(presignedUrl: string, file: File, contentType: string): Observable<void> {
    return new Observable<void>((observer) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', contentType || file.type || 'application/octet-stream');
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next();
          observer.complete();
        } else {
          observer.error(new Error(`Upload failed (status ${xhr.status}).`));
        }
      };
      xhr.onerror = () => observer.error(new Error('Upload failed.'));
      xhr.send(file);
    });
  }

  /** Step 3 — `POST /kbx/kb/add-doc/:kbId` — register the uploaded file. */
  registerDocument(kbId: string, filename: string): Observable<KnowledgeBaseDocDto> {
    return this.#http
      .post<KnowledgeBaseDocDto>(this.#url(`/kbx/kb/add-doc/${encodeURIComponent(kbId)}`), {
        filename,
      })
      .pipe(catchError((err) => this.#throwHttp('register KB document', err)));
  }

  /**
   * Convenience helper — runs the full upload chain and returns the
   * registered document. Callers that want to surface per-step errors
   * can compose the three primitives themselves.
   */
  addDocument(kbId: string, file: File): Observable<KnowledgeBaseDocDto> {
    return this.getUploadUrl(kbId, file.name).pipe(
      switchMap((presigned) =>
        this.uploadFile(
          presigned.presigned_url,
          file,
          presigned.content_type || file.type || 'application/octet-stream',
        ).pipe(switchMap(() => this.registerDocument(kbId, file.name))),
      ),
    );
  }

  /** `DELETE /kbx/kb-doc/:kbId/:docId` — remove a document from a KB. */
  removeDocument(kbId: string, docId: string): Observable<void> {
    return this.#http
      .delete<void>(
        this.#url(`/kbx/kb-doc/${encodeURIComponent(kbId)}/${encodeURIComponent(docId)}`),
      )
      .pipe(catchError((err) => this.#throwHttp('remove KB document', err)));
  }

  // ── Download URL ───────────────────────────────────────────────────

  /**
   * `POST /kbx/kb/get-download-url` — returns a presigned GET URL the
   * browser can hit directly. Use `url#page=N` to jump to a page when
   * rendering inside the PDF viewer.
   */
  getDownloadUrl(bucket: string, filename: string): Observable<string> {
    return this.#http
      .post<PresignedDownloadUrlResponse>(this.#url('/kbx/kb/get-download-url'), {
        bucket,
        filename,
      })
      .pipe(
        map((r) => r.presigned_url),
        catchError((err) => this.#throwHttp('prepare KB document download', err)),
      );
  }

  // ── Search engines ────────────────────────────────────────────────

  /** `GET /kbx/search-engines/` — list every SE visible to the user. */
  listSearchEngines(): Observable<SearchEngineDto[]> {
    return this.#http
      .get<SearchEngineDto[]>(this.#url('/kbx/search-engines/'))
      .pipe(catchError((err) => this.#throwHttp('list search engines', err)));
  }

  /** `GET /kbx/search-engine/:seId` — full SE with linked KB ids. */
  getSearchEngine(seId: string): Observable<SearchEngineDto> {
    return this.#http
      .get<SearchEngineDto>(this.#url(`/kbx/search-engine/${encodeURIComponent(seId)}`))
      .pipe(catchError((err) => this.#throwHttp('load search engine', err)));
  }

  /** `POST /kbx/search-engines/` — create a new SE linked to `datastore_ids`. */
  createSearchEngine(payload: CreateSearchEngineRequest): Observable<SearchEngineDto> {
    return this.#http
      .post<SearchEngineDto>(this.#url('/kbx/search-engines/'), {
        status: 'draft',
        ...payload,
      })
      .pipe(catchError((err) => this.#throwHttp('create search engine', err)));
  }

  /** `DELETE /kbx/search-engines/:seId`. */
  deleteSearchEngine(seId: string): Observable<void> {
    return this.#http
      .delete<void>(this.#url(`/kbx/search-engines/${encodeURIComponent(seId)}`))
      .pipe(catchError((err) => this.#throwHttp('delete search engine', err)));
  }

  /**
   * `POST /kbx/search-engine/:seId/query` — the RAG query endpoint.
   * The `name` field is the SE's display name; matches the legacy
   * payload shape (`{ query, name }`).
   */
  query(seId: string, query: string, name = 'search'): Observable<SearchQueryResponse> {
    return this.#http
      .post<SearchQueryResponse>(
        this.#url(`/kbx/search-engine/${encodeURIComponent(seId)}/query`),
        { query, name },
      )
      .pipe(catchError((err) => this.#throwHttp('run the query', err)));
  }

  /** `GET /kbx/usage` — per-plan References quota state for the current user. */
  getUsage(): Observable<KbUsageDto> {
    return this.#http
      .get<KbUsageDto>(this.#url('/kbx/usage'))
      .pipe(catchError((err) => this.#throwHttp('load References usage', err)));
  }

  // ── Internals ──────────────────────────────────────────────────────

  /** Builds a request context that tells the auth interceptor to skip its header. */
  static skipAuthContext(): HttpContext {
    return new HttpContext().set(SKIP_AUTH_HEADER, true);
  }

  #throwHttp(action: string, err: unknown): Observable<never> {
    console.error(`[KbWebService] Failed to ${action}:`, err);
    const maybeHttp = err as { error?: unknown; message?: string; status?: number };
    const body = maybeHttp?.error as
      | { detail?: unknown; code?: unknown; usage?: unknown }
      | undefined;
    const detail = typeof body?.detail === 'string' ? body.detail : undefined;
    const code = typeof body?.code === 'string' ? body.code : undefined;
    const usage =
      body?.usage && typeof body.usage === 'object' ? (body.usage as KbUsageDto) : undefined;
    const message = detail || maybeHttp?.message || `Failed to ${action}.`;
    return throwError(() => new KbApiError(message, maybeHttp?.status ?? 0, code, usage));
  }
}
